import { AutoSizer, List } from 'react-virtualized';
import { Block, Flex, Title } from '@upsales/components';
import {
	calculateSubaccountHeight,
	calculateTotalVisibleRows,
	computeHeaderColumns,
	generatePreColumns,
	generateTreeStructureFields,
	generateAccountFields,
	generateActionFields,
	Field
} from './helpers';
import AccountRow from '../AccountRow';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import TreeHeader from './TreeHeader';

import './VirtualizedGroupTree.scss';

type VirtualizedGroupTreeProps = {
	flatTree: any[];
	maxDepth: number;
	actions: any;
	type: string;
	idField: string;
	dataSourceId: string;
	addingAccount: any;
	groupAccount: any;
	selected: number[];
	multiselect: boolean;
	viewOnly: boolean;
	showPipelineColumn: boolean;
	hasGroupBonanza: boolean;
	hasSubaccountsV2: boolean;
	rowState: Record<string, { status: string; subaccounts: any[] }>;
	onToggleSubaccounts: (id: string) => void;
	noResults: string;
};

const VirtualizedGroupTree = ({
	flatTree,
	maxDepth,
	actions,
	type,
	idField,
	dataSourceId,
	addingAccount,
	groupAccount,
	selected,
	multiselect,
	viewOnly,
	showPipelineColumn,
	hasGroupBonanza,
	hasSubaccountsV2,
	rowState,
	onToggleSubaccounts,
	noResults
}: VirtualizedGroupTreeProps) => {
	const [headerColumns, setHeaderColumns] = useState<any[]>([]);
	const classes = new BemClass('VirtualizedGroupTree');
	const showPipelineColumnWithBiggerWidth = showPipelineColumn && window.innerWidth > 1500;

	// Can't use useMetadata hook here because the component is wrapped in a non-react component
	const metadata = Tools.AppService.getMetadata();

	const flatTreeKey = useMemo(() => flatTree.map(n => n.id).join(','), [flatTree]);
	const rowStateKey = useMemo(
		() =>
			Object.entries(rowState)
				.map(([id, val]) => `${id}:${val.status}:${val.subaccounts?.length ?? 0}`)
				.join(','),
		[rowState]
	);

	const totalRows = useMemo(() => {
		return calculateTotalVisibleRows(flatTree, rowState);
	}, [flatTreeKey, rowStateKey]);

	useEffect(() => {
		setHeaderColumns(
			computeHeaderColumns(
				showPipelineColumnWithBiggerWidth,
				maxDepth,
				hasGroupBonanza,
				hasSubaccountsV2,
				actions.beginMerge,
				window.innerWidth
			)
		);
	}, [showPipelineColumnWithBiggerWidth, maxDepth, actions.beginMerge]);

	const drawTreeRow = useCallback(
		(node: any, index: number, isSubaccount = false, isLast = false, position: number = 0): ReactNode[] => {
			const level = node.depth ?? position;
			if (addingAccount && parseInt(addingAccount.externalId) === parseInt(node[idField])) {
				node = Object.assign({}, node, addingAccount);
			}
			const hidden = node.hidden;
			const treeIdReal = node.id;
			const rowStateForNode = rowState[treeIdReal] || { status: 'CLOSED', subaccounts: [] };

			let subaccountRows = null;

			const isLastChild = node.isLastChild || isLast;
			const holes = isLastChild ? [level] : [];

			if (rowStateForNode.status === 'OPEN' && rowStateForNode.subaccounts?.length && !hidden) {
				subaccountRows = rowStateForNode.subaccounts.map((subaccount, idx) =>
					drawTreeRow(subaccount, idx, true, idx === rowStateForNode.subaccounts.length - 1, level + 1)
				);
			}

			const nameColumn = dataSourceId === 'prospecting' ? 'nameProspecting' : 'nameSoliditet';
			const relateAnySubaccountEnabled = metadata?.params?.RelateAnySubaccount ?? false;

			const fields: Field[] = [
				...generatePreColumns(node),
				...generateTreeStructureFields(level, maxDepth, isSubaccount, isLastChild, holes).map(field =>
					field.type === 'nameSoliditet' ? { ...field, type: nameColumn } : field
				),
				...generateAccountFields(
					node,
					isSubaccount,
					showPipelineColumnWithBiggerWidth,
					hasGroupBonanza,
					hasSubaccountsV2
				),
				...generateActionFields(node, isSubaccount, viewOnly, dataSourceId, relateAnySubaccountEnabled)
			];

			const row = hidden ? null : (
				<AccountRow
					key={index}
					account={node}
					fields={fields}
					actions={actions}
					groupAccount={groupAccount}
					type={type}
					idField={idField}
					dataSourceId={dataSourceId}
					multiselect={multiselect}
					selected={selected.indexOf(parseInt(node[idField])) !== -1}
					onToggleSubaccounts={() => onToggleSubaccounts(node.id)}
					showSubaccounts={rowStateForNode.status === 'OPEN'}
					isSubaccount={rowStateForNode.subaccounts.length}
					headerColumns={headerColumns}
				/>
			);

			return row ? [row, subaccountRows].flat().filter(Boolean) : [];
		},
		[maxDepth, rowStateKey, showPipelineColumnWithBiggerWidth]
	);

	const memoizedRowRenderer = useCallback(
		({ index, key, style }: { index: number; key: string; style: CSSProperties }) => {
			const processedSubaccounts = new Set();

			let currentIndex = 0;
			let currentNode = null;
			for (const node of flatTree) {
				if (node.hidden || processedSubaccounts.has(node.id)) continue;
				if (currentIndex === index) {
					currentNode = node;
					break;
				}
				currentIndex++;
				if (hasSubaccountsV2) {
					const rowStateForNode = rowState[node.id] || { status: 'CLOSED', subaccounts: [] };
					if (rowStateForNode.status === 'OPEN') {
						rowStateForNode.subaccounts?.forEach(sub => processedSubaccounts.add(sub.id));
					}
				}
			}

			if (!currentNode) return null;

			let position = 0;
			currentIndex = 0;
			processedSubaccounts.clear();

			for (const node of flatTree) {
				if (node.hidden || processedSubaccounts.has(node.id)) continue;
				if (currentIndex === index) break;

				position += 64;
				currentIndex++;

				if (hasSubaccountsV2) {
					const rowStateForNode = rowState[node.id] || { status: 'CLOSED', subaccounts: [] };
					if (rowStateForNode.status === 'OPEN') {
						const visibleSubs = rowStateForNode.subaccounts.filter(sub => !sub.hidden);
						visibleSubs.forEach(sub => {
							processedSubaccounts.add(sub.id);
						});
						position += calculateSubaccountHeight(node, rowState);
					}
				}
			}
			return (
				<Block key={key} style={{ ...style, top: position }}>
					{drawTreeRow(currentNode, index)}
				</Block>
			);
		},
		[flatTreeKey, rowStateKey, drawTreeRow, hasSubaccountsV2]
	);

	return (
		<Block className={`result-table group-tree found-tree ${classes.b()}`}>
			<TreeHeader
				maxDepth={maxDepth}
				showPipelineColumnWithBiggerWidth={showPipelineColumnWithBiggerWidth}
				hasGroupBonanza={hasGroupBonanza}
				hasSubaccountsV2={hasSubaccountsV2}
				actions={actions}
			/>
			{totalRows > 0 ? (
				<AutoSizer>
					{({ height, width }) => (
						<List
							height={height}
							width={width}
							rowCount={totalRows}
							rowHeight={64}
							rowRenderer={memoizedRowRenderer}
						/>
					)}
				</AutoSizer>
			) : (
				<Flex space="ptxl pbxl" alignItems="center" justifyContent="center" direction="column">
					<img alt="upsales briefcase" src="img/empty-briefcase.svg" />
					<Block space="ptl pbl">
						<Title color="grey-10">{noResults}</Title>
					</Block>
				</Flex>
			)}
		</Block>
	);
};

export default VirtualizedGroupTree;
